<template>
  <div v-if="isNotFetching" selenium="pro-account-info" class="m-t-3 m-t-6:t">
    <auth-redirect-form
        ref="form"
        source="edit_profile"
    />

    <vk-modal v-if="showTerms" @close="showTerms = false">
      <div slot="header"/>
      <div slot="content">
        <p class="m-b-1">
          By turning SMS notifications on, I agree that I want to receive texts from Pro Referral.
        </p>
        <p class="m-b-1">
          Pro Referral or parties calling on its behalf may send me automated texts regarding job
          offers, service requests and other special offers. I understand that providing this
          consent is not required or a condition of purchasing any products or services.
        </p>
        <p class="m-b-1">
          I also understand that message frequency may vary depending on numerous factors
          including, but not limited to, customer demand, customer status updates, and the number
          of leads and/or job offers I have at any given time.
        </p>
        <p class="m-b-1">
          I also agree that Pro Referral can send me texts between the hours of 12:00 am and 11:59
          pm. Message and data rates may apply. Not all carriers are supported. For help, text HELP
          to 232266. To stop all messages, text STOP to 232266 or turn SMS notifications off in
          your account settings.
        </p>
        <p>
          For questions, contact 1-855-723-2266. For information on how Pro Referral uses your
          personal information, see our
          <router-link :to="{ name: 'about::legal::privacy' }" class="link">
            Privacy Policy
          </router-link>.
        </p>
      </div>
    </vk-modal>

    <div class="layout gap-1">
      <div class="layout-item is-12 is-6:t">
        <div class="INFO-SECTION-TITLE d-none:mo">My Business Info</div>
        <div class="d-flex fw-nowrap:t fd-column:mo">
          <pro-avatar :size="120" :image="profileImage" class="flex-0-0 m-b-3:mo m-x-auto:mo m-r-3:t"/>
          <div>
            <div class="INFO-LABEL">Business Name</div>
            <div
                class="INFO-CONTENT"
                selenium="pro-account-info-business">
              {{ businessName || 'None' }}
            </div>

            <div class="INFO-LABEL">Name</div>
            <div
                class="INFO-CONTENT"
                selenium="pro-account-info-name">
              {{ fullName || 'None' }}
            </div>

            <div class="INFO-LABEL">Occupation(s)</div>
            <div
                class="INFO-CONTENT"
                selenium="pro-account-info-occupations">
              {{ occupations || 'None' }}
            </div>

            <div class="INFO-LABEL">Email</div>
            <div class="INFO-CONTENT" selenium="pro-account-info-link-email">
              {{ email || 'None' }} <a class="link" @click="submitForm">Edit</a>
            </div>

            <div class="INFO-LABEL">Mobile Phone</div>
            <div class="INFO-CONTENT" selenium="pro-account-info-link-mobilephone">
              {{ smsFormatted || 'None' }} <a class="link" @click="submitForm">Edit</a>
            </div>

            <div class="INFO-LABEL">Work Phone</div>
            <div class="INFO-CONTENT" selenium="pro-account-info-link-workphone">
              {{ phoneFormatted || 'None' }} <a class="link" @click="submitForm">Edit</a>
            </div>

            <div class="INFO-LABEL">Address</div>
            <div
                class="INFO-CONTENT"
                selenium="pro-account-info-address">
              {{ address || 'None' }}
            </div>

            <div class="INFO-LABEL">
              Pro Xtra Number
              <div
                  v-tooltip="{
                    content: proXtraTip,
                    placement: 'bottom',
                    offset: 8
                  }"
                  class="INFO-CIRCLE">
                <vk-icon
                    name="exclamation"
                    size="12"
                />
              </div>
            </div>
            <div
                class="INFO-CONTENT"
                selenium="pro-account-info-proxtra">
              {{ proXtraFormatted || 'None' }}
            </div>

            <div class="INFO-LABEL">Business Website</div>
            <div
                class="INFO-CONTENT"
                selenium="pro-account-info-website">
              {{ website || 'None' }} <a class="link" @click="submitForm">Edit</a>
            </div>
          </div>
        </div>
      </div>
      <div class="BORDER-LEFT layout-item is-12 is-6:t">
        <div class="INFO-SECTION-TITLE text-center">Change Password</div>
        <app-change-password-form :show-title="false" class="m-b-3:mo m-x-auto"/>

        <div class="INFO-DIVIDER"/>

        <div class="INFO-NOTIFICATIONS">
          <div class="INFO-SECTION-TITLE text-center">Notification Settings</div>
          <div class="d-flex jc-between ai-center">
            <span>SMS Notifications
              <div
                  v-tooltip="{
                    content: smsTip,
                    placement: 'bottom',
                    offset: 8
                  }"
                  class="INFO-CIRCLE">
                <vk-icon
                    name="exclamation"
                    size="12"
                />
              </div>
            </span>
            <div class="switch" selenium="pro-account-info-toggle">
              <input id="smsNotifications" v-model="smsNotifications" type="checkbox" :disabled="isSMSNotificationsSwitchDisabled">
              <label for="smsNotifications"/>
            </div>
          </div>
          <div v-if="!smsNotifications" class="text-sm m-t-1">
            By turning SMS notifications on, you agree to receive periodic automated texts
            regarding your participation in Pro Referral.
            Consent is not required or a condition of purchase. Message and data rates may apply.
            Message frequency varies.
            Reply STOP to unsubscribe or HELP for help.
            <span class="link" @click="showTerms = true">Terms & Conditions</span>
            ;<router-link :to="{ name: 'about::legal::privacy' }" class="link PRIVACY">
              Privacy and Security Statement
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppChangePasswordForm from '@/components/AppChangePasswordForm';
import AuthRedirectForm from '@/components/AuthRedirectForm';
// TODO - put this back in once vue-kit rollup is released
// import PhoneUtils from '@homeservices/vue-kit/utils/phone-utils';

export default {
  name: 'account-info',
  components: {
    AppChangePasswordForm,
    AuthRedirectForm,
  },
  data () {
    return {
      proXtraTip: 'If you want to change your Pro Xtra Number, please call 1-855-723-2266',
      smsTip: 'Mobile number required to enable SMS notifications',
      showTerms: false,
    };
  },
  computed: {
    ...mapGetters('user', [
      'fullName',
      'profileImage',
    ]),
    ...mapGetters('account/pro', [
      'occupationNames',
      'isFetching',
    ]),
    ...mapGetters('account/pro/info', [
      'address',
      'businessName',
      'email',
      'phone',
      'sms',
      'smsNotificationEnabled',
      'website',
      'proXtraNumber',
    ]),
    smsNotifications: {
      get () {
        return this.smsNotificationEnabled;
      },
      set (value) {
        this.$store.dispatch('account/pro/info/updateSmsNotification', value);
      },
    },
    proXtraFormatted () {
      // TODO - put this back in once vue-kit rollup is released
      // return this.proXtraNumber ? PhoneUtils.format(this.proXtraNumber) : '';
      return this.$options.filters.phoneNumber(this.proXtraNumber);
    },
    phoneFormatted () {
      // TODO - put this back in once vue-kit rollup is released
      // return this.phone ? PhoneUtils.format(this.phone) : '';
      return this.$options.filters.phoneNumber(this.phone);
    },
    smsFormatted () {
      // TODO - put this back in once vue-kit rollup is released
      // return this.sms ? PhoneUtils.format(this.sms) : '';
      return this.$options.filters.phoneNumber(this.sms);
    },
    occupations () {
      return this.occupationNames.join(', ');
    },
    isSMSNotificationsSwitchDisabled () {
      return !this.sms;
    },
    isNotFetching () {
      return !this.isFetching;
    },
  },
  watch: {
    isNotFetching (val) {
      if (!this.sms && val) {
        this.smsNotifications = false;
      }
    },
  },
  async mounted () {
    await this.$store.dispatch('account/pro/info/fetch');
  },
  methods: {
    submitForm () {
      this.$refs.form.submit();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .INFO-SECTION-TITLE {
    @include margin-bottom(3);
    @include h1();
    font-weight: bold;
  }

  .INFO-LABEL {
    color: $color-gray60;
    font-weight: bold;
  }

  .INFO-CONTENT {
    @include margin-bottom(3);
  }

  .INFO-DIVIDER {
    @include margin(6 auto);
    display: none;
    width: 80%;
    border-top: 1px solid $color-gray60;

    @include tablet {
      display: block;
    }
  }

  .INFO-NOTIFICATIONS {
    @include margin(0 auto);
    @include responsive(width, 100%, 350px);
  }

  .INFO-CIRCLE {
    @include square(18px);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    background-color: $color-gray80;
    border-radius: 50%;
  }

  .BORDER-LEFT {
    @include tablet {
      border-left: 1px solid $color-gray60
    }
  }
  .link:visited {
    color: $color-depot;
  }
</style>
